import React, { useState, useCallback, useEffect } from 'react';
import i18n from 'i18n';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { uniqBy } from 'lodash';

// Import components
import ToastError from './components/ToastError';

// Import helpers
import { bytesToSize, excludedErrors } from './helpers';

const toastStyles = {
	borderRadius: '10px',
	background: '#131822',
	color: '#fff',
	border: '1px solid #2C343D'
};

function useAttachments({ clear, maxTotalSize, maxFileCount }) {
	const [files, setFiles] = useState([]);

	const appendFiles = (newFiles) => {
		setFiles((current) =>
			uniqBy([...current, ...newFiles], (element) => element.path)
		);
	};

	const removeFile = (name) => {
		setFiles(files.filter((current) => current?.name !== name));
	};

	const onDropAccepted = (arg) => {
		const errors = [];
		const filesToAppend = new Set();
		let prevFilesSize = files.reduce(
			(state, current) => state + current.size,
			0
		);
		arg.forEach((file) => {
			if (file.size + prevFilesSize <= maxTotalSize) {
				if (filesToAppend.size + files.length < maxFileCount) {
					prevFilesSize += file.size;
					filesToAppend.add(file);
				} else {
					errors.push({
						code: 'max-total-file-count',
						message: i18n.t('contact_form_errror_item_count', {
							count: maxFileCount
						})
					});
				}
			} else {
				errors.push({
					code: 'max-total-size-exceeded',
					message: i18n.t('contact_form_error_total_size_too_large', {
						size: bytesToSize(maxTotalSize)
					})
				});
			}
		});
		appendFiles([...filesToAppend]);
		uniqBy(errors, (error) => error.code).forEach((err) =>
			toast(({ id }) => <ToastError id={id} err={[err]} />, {
				duration: 5000,
				position: 'bottom-right',
				style: toastStyles
			})
		);
	};

	const onDropRejected = useCallback((arg) => {
		const errorsToNotify = arg
			.map(({ file, errors }) => ({
				fileName: file?.name,
				err: errors.filter(({ code }) => !excludedErrors.includes(code))
			}))
			.filter(({ err }) => err.length);
		errorsToNotify.forEach((error) => {
			toast(({ id }) => <ToastError id={id} {...error} />, {
				duration: 5000,
				position: 'bottom-right',
				style: toastStyles
			});
		});
	}, []);

	useEffect(() => {
		setFiles([]);
	}, [clear]);

	return {
		files,
		setFiles,
		onDropAccepted,
		onDropRejected,
		appendFiles,
		removeFile
	};
}

useAttachments.propTypes = {
	clear: PropTypes.bool,
	maxTotalSize: PropTypes.number,
	maxFileCount: PropTypes.number
};

export default useAttachments;
